import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import Input from 'components/Input';
import Button, { ButtonType } from 'components/Button';
import FormNavigation from 'components/FormNavigation';
import FormContainer from 'components/LoanForm/FormContainer';
import { FlowComponentType } from 'routes/types';
import { ConfirmPrefillDetailsResult } from 'enums/FlowNextResults';
import { getAuthData } from 'selectors/getAuthData';
import { getYourContactData } from 'selectors/getYourContact';
import { getYourNameData } from 'selectors/yourName';
import { getYourBirthDateData } from 'selectors/yourBirthDate';
import { getYourAddressData } from 'selectors/yourAddress';
import { YourDetailsVariable } from 'enums/LoanFormVariables';
import { authSessionThunk } from 'thunks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useMethodAuth from 'hooks/useMethodAuth';
import { CreateAuthSessionResponse, ErrorType as MethodErrorType } from 'api/MethodApi';
import { getMethodAuthData } from 'selectors/methodAuth';

import styles from './ConfirmPrefillDetails.module.scss';

const ConfirmPrefillDetails = ({ navigationInfo, handleNext }: FlowComponentType): JSX.Element => {
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { processMethodErrorAndRedirect } = useMethodAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { prefill, sessionToken } = useSelector(getAuthData);
  const { phone_number: phoneNumber } = useSelector(getYourContactData);
  const { first_name: firstName, last_name: lastName } = useSelector(getYourNameData);
  const { birth_date: birthDate } = useSelector(getYourBirthDateData);
  const yourAddress = useSelector(getYourAddressData);
  const { isLoading } = useSelector(getMethodAuthData);

  const masked = prefill?.masked;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await dispatchWithUnwrap(
        authSessionThunk({
          phoneNumber,
          firstName,
          lastName,
          birthDate,
          address: {
            street: `${yourAddress[YourDetailsVariable.AddressLine1] ?? ''} ${
              yourAddress[YourDetailsVariable.AddressLine2] ?? ''
            }`,
            city: yourAddress[YourDetailsVariable.City],
            stateOrProvince: yourAddress[YourDetailsVariable.ArgyleState],
            zipOrPostalCode: yourAddress[YourDetailsVariable.ZipCode],
          },
          sessionToken: sessionToken!,
          SSN4: prefill?.last4SSN ?? '',
        }),
      );
      handleNext(ConfirmPrefillDetailsResult.Confirmed);
    } catch (e) {
      const { errorType, entityId: currentEntityId } = e as CreateAuthSessionResponse;
      if (processMethodErrorAndRedirect(handleNext, errorType, false, true, currentEntityId)) {
        if (errorType === MethodErrorType.InvalidZipCode) {
          handleNext(ConfirmPrefillDetailsResult.UpdateFields);
        }
        return;
      }
      handleNext(ConfirmPrefillDetailsResult.Error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <FormNavigation {...navigationInfo} />
      <FormContainer
        title="Confirm your details"
        subtitle="We found this information associated with your phone number. Please confirm it looks correct."
      >
        <div className={styles.inputsContainer}>
          <Input label="Name" name="name" value={masked?.name || `${firstName} ${lastName}`} disabled />
          <Input label="Phone" name="phone" value={masked?.phone || ''} disabled />
          {masked?.email ? <Input label="Email" name="email" value={masked.email} disabled /> : null}
          {masked?.dob ? <Input label="Date of birth" name="dob" value={masked.dob} disabled /> : null}
          {masked?.address ? <Input label="Address" name="address" value={masked.address} disabled /> : null}
          {masked?.ssn ? <Input label="SSN" name="ssn" value={masked.ssn} disabled /> : null}
        </div>

        <div className={styles.buttonsContainer}>
          <Button onClick={handleConfirm} className={styles.button} isLoading={isSubmitting || isLoading}>
            This is correct
          </Button>
          <Button
            type={ButtonType.Inverted}
            onClick={() => handleNext(ConfirmPrefillDetailsResult.UpdateFields)}
            className={styles.button}
            disabled={isSubmitting || isLoading}
          >
            Make a change
          </Button>
        </div>
      </FormContainer>
    </div>
  );
};

export default ConfirmPrefillDetails;
